import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    useTheme,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { request } from "app/network";

// Define the interface for the component props
import { ICommonCode } from "Shared/Interfaces/ICommonCode";

interface PointGrantDialogProps {
    open: boolean;
    onClose: () => void;
    userSeqId: number;
    onSuccess: () => void;
}

const PointGrantDialog: React.FC<PointGrantDialogProps> = ({
    open,
    onClose,
    userSeqId,
    onSuccess,
}) => {
    const theme = useTheme();
    const darkMode = theme.palette.mode === "dark";
    const [loading, setLoading] = useState(false);
    const [earnCodes, setEarnCodes] = useState<ICommonCode[]>([]);

    // Form State
    const [selectedEarnCode, setSelectedEarnCode] = useState<string>("");
    const [amount, setAmount] = useState<string>("");
    const [description, setDescription] = useState<string>("");

    useEffect(() => {
        if (open) {
            loadCommonCodes();
            // Reset form on open
            setSelectedEarnCode("");
            setAmount("");
            setDescription("");
        }
    }, [open]);

    const loadCommonCodes = async () => {
        try {
            const response = await request("CommonCode.getByGroup", {
                pathParams: { groupCode: "POINT_CONDITION_TYPE" },
            });
            if (response && response.data) {
                // Filter only codes that start with 'POINT_EARN_'
                const filteredCodes = response.data.filter((item: any) =>
                    item.code && item.code.startsWith("POINT_EARN_")
                );
                setEarnCodes(filteredCodes);
            }
        } catch (error) {
            console.error("Failed to load common codes", error);
            alert("공통 코드를 불러오는데 실패했습니다.");
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!selectedEarnCode) {
            alert("지급 사유를 선택해주세요.");
            return;
        }
        if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
            alert("유효한 포인트를 입력해주세요.");
            return;
        }

        setLoading(true);
        try {
            // Assuming PointTransactionAPI has a grantPoint method or similar. 
            // Since the backend controller isn't connected yet in the user's instructions,
            // this might fail if the endpoint doesn't exist. 
            // We will assume the existence of a 'grant' endpoint or similar generic create.

            // Based on typical patterns in this project:
            await request("PointTransaction.grantPoint", {
                paramz: {
                    userSeqId: userSeqId,
                    amount: Number(amount),
                    reasonCode: selectedEarnCode,
                    description: description,
                },
            });

            setLoading(false);
            alert("포인트가 성공적으로 지급되었습니다.");
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Failed to grant points", error);
            alert("포인트 지급에 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle component="div">
                <MDTypography variant="h5" fontWeight="medium">
                    포인트 지급
                </MDTypography>
            </DialogTitle>
            <DialogContent dividers>
                <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                    <MDInput
                        select
                        label="지급 사유 *"
                        value={selectedEarnCode}
                        onChange={(e: any) => setSelectedEarnCode(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            "& .MuiInputBase-root": {
                                minHeight: "44.13px", // Match standard input height
                                padding: "10px 12px", // Adjust padding for vertical centering
                            },
                        }}
                    >
                        {earnCodes.map((code, index) => (
                            <MenuItem key={`${code.code}-${index}`} value={code.code}>
                                {code.name}
                            </MenuItem>
                        ))}
                    </MDInput>

                    <MDInput
                        label="지급 포인트 *"
                        type="number"
                        value={amount}
                        onChange={(e: any) => setAmount(e.target.value)}
                        fullWidth
                        helperText="0보다 큰 정수를 입력해주세요."
                        sx={{
                            "& input[type=number]": {
                                colorScheme: darkMode ? "dark" : "normal",
                            },
                        }}
                    />

                    <MDInput
                        label="메모 (선택사항)"
                        value={description}
                        onChange={(e: any) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                    />
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} color="dark">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSubmit}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? "처리중..." : "지급하기"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default PointGrantDialog;
